/* GENERATED FILE — DO NOT EDIT.
 *
 * Emitted from `contracts/openapi/reconciliation.yaml` by `tools/openapi_ts/generate.py`.
 * Regenerate with `python -m tools.openapi_ts.generate`;
 * `test_generated_client_matches_the_contract` fails the build if this file drifts from the spec.
 *
 * Hard rule 10 makes the spec authoritative, so editing this file by hand would put the client and
 * the server behind two different sources of truth — which is the drift the rule exists to prevent.
 *
 * Each type has a matching `is<Name>` guard. The types are for `tsc`; the guards are because a
 * response is data from another process, and a compile-time type is only a claim about it.
 */


export type Cadence = 'daily' | 'weekly';

export const CADENCES: readonly Cadence[] = ['daily', 'weekly'];

export function isCadence(value: unknown): value is Cadence {
  return (['daily', 'weekly'] as readonly unknown[]).includes(value);
}

/**
 * What happened the last time the schedule came due. The separations are the point: collapsing any
 * pair would make a schedule that is working-as-configured indistinguishable from one quietly
 * doing nothing. `requested` means a run was queued — **not** that it succeeded, which the run
 * itself owns. `deferred` is the moment being wrong (a run was already in flight) and needs
 * nobody. `refused` is the platform declining and needs somebody. `failed` is ours. `skipped`
 * means the definition was gone.
 */
export type ScheduleOutcome = 'requested' | 'deferred' | 'refused' | 'failed' | 'skipped';

export const SCHEDULE_OUTCOMES: readonly ScheduleOutcome[] = ['requested', 'deferred', 'refused', 'failed', 'skipped'];

export function isScheduleOutcome(value: unknown): value is ScheduleOutcome {
  return (['requested', 'deferred', 'refused', 'failed', 'skipped'] as readonly unknown[]).includes(value);
}

/**
 * How loud the incident is when a run finds the two sides disagreeing. Defaults to `warning`,
 * never `critical`: defaulting to the loudest teaches people to ignore the loudest, which spends
 * the one severity that should interrupt somebody.
 */
export type BreakSeverity = 'info' | 'warning' | 'critical';

export const BREAK_SEVERITYS: readonly BreakSeverity[] = ['info', 'warning', 'critical'];

export function isBreakSeverity(value: unknown): value is BreakSeverity {
  return (['info', 'warning', 'critical'] as readonly unknown[]).includes(value);
}

export type ToleranceKind = 'exact' | 'absolute' | 'relative' | 'fuzzy';

export const TOLERANCE_KINDS: readonly ToleranceKind[] = ['exact', 'absolute', 'relative', 'fuzzy'];

export function isToleranceKind(value: unknown): value is ToleranceKind {
  return (['exact', 'absolute', 'relative', 'fuzzy'] as readonly unknown[]).includes(value);
}

/**
 * Two terminal outcomes are refusals rather than failures, and both exist because a run that
 * cannot mean what it would appear to mean is worse than no run. `keys_disjoint` — the sketch gate
 * measured cheaply, concluded the key configuration is almost certainly wrong, and declined the
 * join. The estimates are on the run. `keys_not_unique` — a key repeats on at least one side, so
 * the join would fan out and inflate every count together. No number in such a result looks wrong,
 * which is precisely why it is refused; the row counts and distinct-key counts are in the failure
 * message.
 */
export type RunState = 'pending' | 'running' | 'succeeded' | 'failed' | 'keys_disjoint' | 'keys_not_unique';

export const RUN_STATES: readonly RunState[] = ['pending', 'running', 'succeeded', 'failed', 'keys_disjoint', 'keys_not_unique'];

export function isRunState(value: unknown): value is RunState {
  return (['pending', 'running', 'succeeded', 'failed', 'keys_disjoint', 'keys_not_unique'] as readonly unknown[]).includes(value);
}

export type BreakKind = 'left_only' | 'right_only' | 'mismatch';

export const BREAK_KINDS: readonly BreakKind[] = ['left_only', 'right_only', 'mismatch'];

export function isBreakKind(value: unknown): value is BreakKind {
  return (['left_only', 'right_only', 'mismatch'] as readonly unknown[]).includes(value);
}

export interface SideSpec {
  readonly qualified_name: string;
  readonly key_columns: readonly string[];
}

export function isSideSpec(value: unknown): value is SideSpec {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['qualified_name'] === 'string' &&
    Array.isArray(candidate['key_columns']) &&
    (candidate['key_columns'] as readonly unknown[]).every((item) => typeof item === 'string')
  );
}

export interface FieldTolerance {
  readonly left_column: string;
  readonly right_column: string;
  readonly kind: ToleranceKind;
  /**
   * Absent for `exact`. Positive for `absolute` (the field's own unit). In (0, 1) for `relative`
   * (a ratio — 0.01 is 1%) and (0, 1] for `fuzzy` (a similarity threshold). The ratio constraint
   * is held at the substrate: a percentage typo is refused, not silently 100x.
   */
  readonly bound?: number | null;
}

export function isFieldTolerance(value: unknown): value is FieldTolerance {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['left_column'] === 'string' &&
    typeof candidate['right_column'] === 'string' &&
    (['exact', 'absolute', 'relative', 'fuzzy'] as readonly unknown[]).includes(candidate['kind'])
  );
}

export interface ToleranceVersion {
  readonly id: string;
  readonly version: number;
  readonly published_by: string;
  readonly published_at: string;
  readonly fields: readonly FieldTolerance[];
}

export function isToleranceVersion(value: unknown): value is ToleranceVersion {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['version'] === 'number' &&
    typeof candidate['published_by'] === 'string' &&
    typeof candidate['published_at'] === 'string' &&
    Array.isArray(candidate['fields']) &&
    (candidate['fields'] as readonly unknown[]).every((item) => isFieldTolerance(item))
  );
}

/**
 * What `POST /definitions` accepts. A definition and its first tolerance version are created in
 * one act, because a definition without tolerances cannot run and a two-step creation invites the
 * half-made state — so `tolerances` is required here rather than added afterwards.
 */
export interface DefinitionWrite {
  /** Unique per tenant, compared case- and space-insensitively. */
  readonly name: string;
  /**
   * The left side's connection, and the right side's too unless `right_connection_id` says
   * otherwise.
   */
  readonly connection_id: string;
  /**
   * The connection the **right** side is read through, when it differs from the left's — Snowflake
   * against Databricks, say. Omit it (or send `null`) for two tables on one connection, which is
   * compared by a single join at the source. A definition naming two different connections is
   * compared by **digest**: each source reports per-slice row counts and checksums and only slices
   * that disagree are narrowed, so no table's rows leave its source. **Every tolerance kind is
   * accepted** (ADR-014): a key whose field differs under `absolute`, `relative` or `fuzzy` is
   * judged from masked evidence each source computes for itself — masked integers whose difference
   * is the distance, and keyed trigram hashes — so no value crosses either connection. A break
   * carries `numeric_delta` for `absolute` and `relative` exactly as on one connection.
   * Cross-connection `fuzzy` is the trigram Jaccard index of the two values (lower-cased, padded
   * as `pg_trgm` pads), because no engine's native similarity function can see a string held by
   * another engine; a value longer than 512 characters is judged a disagreement. A numeric value
   * must be below 10^14 in magnitude to be compared, and is compared at ten decimal places.
   * Whether each source can compute the evidence is asked at run request time: a source that
   * cannot is a 422 naming it, never a silent exact comparison. Columns are matched by the names
   * each tolerance pair declares (`left_column`, `right_column`), so differently named columns
   * need no separate mapping. Types are not compared: a pair whose text renderings differ across
   * engines (floating point, timestamp precision) reports as mismatches on that field.
   */
  readonly right_connection_id?: string | null;
  readonly left: SideSpec;
  readonly right: SideSpec;
  readonly tolerances: readonly FieldTolerance[];
}

export function isDefinitionWrite(value: unknown): value is DefinitionWrite {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['name'] === 'string' &&
    typeof candidate['connection_id'] === 'string' &&
    candidate['left'] !== undefined &&
    candidate['right'] !== undefined &&
    Array.isArray(candidate['tolerances']) &&
    (candidate['tolerances'] as readonly unknown[]).every((item) => isFieldTolerance(item))
  );
}

export interface Definition {
  readonly id: string;
  readonly name: string;
  /**
   * The left side's connection — and the right side's too when `right_connection_id` is null,
   * which is the original shape: one join, executed where both tables are reachable.
   */
  readonly connection_id: string;
  /**
   * The right side's connection when it differs from `connection_id`; **null means "the same
   * connection"**, which is what every definition created before this field existed means — not
   * "unknown".
   */
  readonly right_connection_id: string | null;
  /**
   * True when the two sides are read through different connections and are therefore compared by
   * digest and masked evidence rather than by join. Every tolerance kind is honoured on both
   * shapes. Served so a client does not re-derive it from two ids.
   */
  readonly is_cross_connection: boolean;
  readonly left: SideSpec;
  readonly right: SideSpec;
  readonly created_by: string;
  readonly created_at: string;
  /** The latest version on a single read; null in list responses. */
  readonly tolerance_version: ToleranceVersion | null;
}

export function isDefinition(value: unknown): value is Definition {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['name'] === 'string' &&
    typeof candidate['connection_id'] === 'string' &&
    (typeof candidate['right_connection_id'] === 'string' || candidate['right_connection_id'] === null) &&
    typeof candidate['is_cross_connection'] === 'boolean' &&
    candidate['left'] !== undefined &&
    candidate['right'] !== undefined &&
    typeof candidate['created_by'] === 'string' &&
    typeof candidate['created_at'] === 'string' &&
    (candidate['tolerance_version'] === null || isToleranceVersion(candidate['tolerance_version']))
  );
}

/**
 * One definition's decision about what a disagreement is worth telling somebody. Held apart from
 * the definition because a definition is immutable — runs cite it, so a definition whose sides
 * changed under its history would make "what did the March run compare" unanswerable — while this
 * is a routing decision a tenant retunes as they learn how noisy a particular reconciliation is.
 */
export interface BreakPolicy {
  readonly definition_id: string;
  readonly enabled: boolean;
  readonly severity: BreakSeverity;
  readonly min_breaks: number;
  /**
   * **False means nobody has chosen this and the platform default applies** — which still opens an
   * incident at `warning` on the first break, because a default of silence would ship the
   * mechanism switched off. Render it: presenting a default as somebody's decision is how a tenant
   * comes to believe a threshold was set that nobody set.
   */
  readonly declared: boolean;
  /** Null exactly when `declared` is false; a default has no author. */
  readonly configured_by: string | null;
  readonly configured_at: string | null;
}

export function isBreakPolicy(value: unknown): value is BreakPolicy {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['definition_id'] === 'string' &&
    typeof candidate['enabled'] === 'boolean' &&
    (['info', 'warning', 'critical'] as readonly unknown[]).includes(candidate['severity']) &&
    typeof candidate['min_breaks'] === 'number' &&
    typeof candidate['declared'] === 'boolean' &&
    (typeof candidate['configured_by'] === 'string' || candidate['configured_by'] === null) &&
    (typeof candidate['configured_at'] === 'string' || candidate['configured_at'] === null)
  );
}

/**
 * One definition's unattended run schedule, in the customer's own clock. A reconciliation that
 * only runs when somebody presses a button is not a control: in BFSI it is a daily check, and a
 * check performed when remembered stops being performed the week somebody is on leave.
 */
export interface RunSchedule {
  readonly definition_id: string;
  readonly configured: boolean;
  readonly enabled: boolean;
  readonly cadence: Cadence;
  readonly local_time: string;
  readonly timezone: string;
  readonly weekdays: readonly number[];
  /**
   * The cadence as a sentence. Served rather than assembled by each client, so it reads the same
   * on every screen that shows it.
   */
  readonly describes: string;
  /**
   * Null for a disabled schedule, and null while an occurrence is being dispatched — the claim
   * nulls it and the outcome recomputes it, so the two can never both be true.
   */
  readonly next_run_at: string | null;
  readonly last_run_at: string | null;
  /**
   * Null for a schedule that has never come due. **Not an outcome** — a client that reads absence
   * as a result would report "we have not tried" as something that happened.
   */
  readonly last_outcome: ScheduleOutcome | null;
  /** The sentence behind `last_outcome`. Null exactly when that is. */
  readonly last_detail: string | null;
  /**
   * Set when consecutive occurrences were refused, so the schedule stops writing nightly refusals.
   * It resumes by itself; travels with `suspension_reason`.
   */
  readonly suspended_until: string | null;
  readonly suspension_reason: string | null;
}

export function isRunSchedule(value: unknown): value is RunSchedule {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['definition_id'] === 'string' &&
    ([true] as readonly unknown[]).includes(candidate['configured']) &&
    typeof candidate['enabled'] === 'boolean' &&
    (['daily', 'weekly'] as readonly unknown[]).includes(candidate['cadence']) &&
    typeof candidate['local_time'] === 'string' &&
    typeof candidate['timezone'] === 'string' &&
    Array.isArray(candidate['weekdays']) &&
    (candidate['weekdays'] as readonly unknown[]).every((item) => typeof item === 'number') &&
    typeof candidate['describes'] === 'string' &&
    (typeof candidate['next_run_at'] === 'string' || candidate['next_run_at'] === null) &&
    (typeof candidate['last_run_at'] === 'string' || candidate['last_run_at'] === null) &&
    (candidate['last_outcome'] === null || isScheduleOutcome(candidate['last_outcome'])) &&
    (typeof candidate['last_detail'] === 'string' || candidate['last_detail'] === null) &&
    (typeof candidate['suspended_until'] === 'string' || candidate['suspended_until'] === null) &&
    (typeof candidate['suspension_reason'] === 'string' || candidate['suspension_reason'] === null)
  );
}

/** What `GET` returns when nobody has configured a schedule. */
export interface NoRunSchedule {
  readonly definition_id: string;
  readonly configured: boolean;
  readonly enabled: boolean;
  /**
   * Why there is nothing to show, in words — reconciliations run only when somebody asks. Stated
   * rather than left to be inferred from missing fields.
   */
  readonly detail: string;
}

export function isNoRunSchedule(value: unknown): value is NoRunSchedule {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['definition_id'] === 'string' &&
    ([false] as readonly unknown[]).includes(candidate['configured']) &&
    ([false] as readonly unknown[]).includes(candidate['enabled']) &&
    typeof candidate['detail'] === 'string'
  );
}

export interface Run {
  readonly id: string;
  readonly definition_id: string;
  /**
   * The version that judged this run — required and frozen, because a reconciliation result is
   * meaningless without it.
   */
  readonly tolerance_version_id: string;
  readonly state: RunState;
  readonly requested_by: string;
  readonly requested_at: string;
  readonly started_at?: string | null;
  readonly finished_at?: string | null;
  readonly failure?: string | null;
  readonly left_rows?: number | null;
  readonly right_rows?: number | null;
  readonly matched?: number | null;
  readonly mismatched?: number | null;
  readonly left_only?: number | null;
  readonly right_only?: number | null;
  /** The KMV sketch estimates. Exact below the sketch size (k=1024); within a few percent above it. */
  readonly left_estimate?: number | null;
  readonly right_estimate?: number | null;
  readonly overlap_estimate?: number | null;
  readonly jaccard_estimate?: number | null;
  readonly breaks_truncated: boolean;
}

export function isRun(value: unknown): value is Run {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['definition_id'] === 'string' &&
    typeof candidate['tolerance_version_id'] === 'string' &&
    (['pending', 'running', 'succeeded', 'failed', 'keys_disjoint', 'keys_not_unique'] as readonly unknown[]).includes(candidate['state']) &&
    typeof candidate['requested_by'] === 'string' &&
    typeof candidate['requested_at'] === 'string' &&
    typeof candidate['breaks_truncated'] === 'boolean'
  );
}

export interface Break {
  readonly id: string;
  readonly kind: BreakKind;
  /** MD5 of the row's key, computed at the source. */
  readonly key_digest: string;
  /**
   * First-2/last-2 preview rendered at the source in the SELECT list; keys of four characters or
   * fewer arrive fully starred.
   */
  readonly masked_key: string;
  /** The field pairs that disagreed, by left column name. Empty for missing-row breaks. */
  readonly fields: readonly string[];
  /**
   * left minus right for the first failing numeric-tolerance field — a distance, never a value.
   * Null for missing rows and string disagreements.
   */
  readonly numeric_delta: number | null;
}

export function isBreak(value: unknown): value is Break {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    (['left_only', 'right_only', 'mismatch'] as readonly unknown[]).includes(candidate['kind']) &&
    typeof candidate['key_digest'] === 'string' &&
    typeof candidate['masked_key'] === 'string' &&
    Array.isArray(candidate['fields']) &&
    (candidate['fields'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    (typeof candidate['numeric_delta'] === 'number' || candidate['numeric_delta'] === null)
  );
}

export type InvestigationScope = 'mismatched' | 'missing' | 'all';

export const INVESTIGATION_SCOPES: readonly InvestigationScope[] = ['mismatched', 'missing', 'all'];

export function isInvestigationScope(value: unknown): value is InvestigationScope {
  return (['mismatched', 'missing', 'all'] as readonly unknown[]).includes(value);
}

export interface InvestigationQuery {
  readonly break_kind: BreakKind;
  /**
   * Which connection to run it on. `both` is one statement over both tables, and occurs only when
   * they share a connection.
   */
  readonly side: 'both' | 'left' | 'right';
  readonly connection_id: string;
  readonly connection_name: string;
  /** The engine the SQL is written for, e.g. `Snowflake`. */
  readonly engine: string;
  readonly datasets: readonly string[];
  readonly key_count: number;
  /** The disagreeing fields the statement selects, by left column name. */
  readonly fields: readonly string[];
  /**
   * The statement, with comments saying where to run it. Text only — Lumiq never executes it and
   * never sees its results.
   */
  readonly sql: string;
}

export function isInvestigationQuery(value: unknown): value is InvestigationQuery {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['left_only', 'right_only', 'mismatch'] as readonly unknown[]).includes(candidate['break_kind']) &&
    (['both', 'left', 'right'] as readonly unknown[]).includes(candidate['side']) &&
    typeof candidate['connection_id'] === 'string' &&
    typeof candidate['connection_name'] === 'string' &&
    typeof candidate['engine'] === 'string' &&
    Array.isArray(candidate['datasets']) &&
    (candidate['datasets'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    typeof candidate['key_count'] === 'number' &&
    Array.isArray(candidate['fields']) &&
    (candidate['fields'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    typeof candidate['sql'] === 'string'
  );
}

export interface Investigation {
  readonly run_id: string;
  readonly definition_id: string;
  readonly scope: InvestigationScope;
  readonly all_columns: boolean;
  readonly cross_connection: boolean;
  readonly break_count: number;
  /**
   * False when the run recorded only the first 200 breaks; the statements then select those keys,
   * not every row the run's counts describe.
   */
  readonly covers_every_break: boolean;
  readonly queries: readonly InvestigationQuery[];
}

export function isInvestigation(value: unknown): value is Investigation {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['run_id'] === 'string' &&
    typeof candidate['definition_id'] === 'string' &&
    (['mismatched', 'missing', 'all'] as readonly unknown[]).includes(candidate['scope']) &&
    typeof candidate['all_columns'] === 'boolean' &&
    typeof candidate['cross_connection'] === 'boolean' &&
    typeof candidate['break_count'] === 'number' &&
    typeof candidate['covers_every_break'] === 'boolean' &&
    Array.isArray(candidate['queries']) &&
    (candidate['queries'] as readonly unknown[]).every((item) => isInvestigationQuery(item))
  );
}
